import { Checkbox, FormGroup, Select, SelectItem, TextArea, TextInput, Toggle } from '@carbon/react';
import { LANGUAGES, useTranslation } from '@pos/i18n';
import { useContext, useEffect, useState } from 'react';
import { AdminContext } from '../adminContext';
import { languageName } from './languages';
import { WEEKDAYS, type FieldDef } from '../resources';
import { DateField } from './DateField';
import { isEmptyValue, useMarkedLabel, type RequiredMarking } from './required';
import { ReferenceComboBox } from './ReferenceComboBox';
import { useAllowedValues } from './references';


interface FieldInputProps {
  field: FieldDef;
  value: unknown;
  /** A server or form error for this field; wins over the required check. */
  invalidText?: string;
  /** Which fields carry a suffix, see `requiredMarking` (default: required ones get "(required)"). */
  marking?: RequiredMarking;
  /** The form was submitted: show "required" on an empty required field even if it was never visited. */
  submitted?: boolean;
  /** Not editable (e.g. the base unit's factor); the value is still shown and sent. */
  disabled?: boolean;
  /** The whole form, for a field whose choices depend on another field (`FieldDef.limitTo`). */
  values?: Readonly<Record<string, unknown>>;
  onChange(value: unknown): void;
}

/**
 * One form field of a generated resource, by type. A required field left empty is flagged once it loses focus or
 * the form is submitted.
 */
export function FieldInput(props: FieldInputProps) {
  const { t } = useTranslation();
  const { field, value, invalidText, submitted } = props;
  const [touched, setTouched] = useState(false);
  const missing =
    Boolean(field.required) && isEmptyValue(value) && field.type !== 'toggle' && field.type !== 'weekdays';
  const error = invalidText ?? (missing && (touched || submitted) ? t('admin.validation.required') : undefined);
  return (
    <div
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setTouched(true);
      }}
    >
      <FieldControl {...props} invalidText={error} />
    </div>
  );
}

function FieldControl({ field, value, invalidText, marking, disabled, values, onChange }: FieldInputProps) {
  const { t } = useTranslation();
  const id = `field-${field.key}`;
  const marked = useMarkedLabel();
  const labelText = marked(t(field.label), field.required, marking);
  const helperText = field.helper ? t(field.helper) : undefined;
  const common = {
    id,
    labelText,
    invalid: Boolean(invalidText),
    invalidText,
    required: Boolean(field.required),
    helperText,
    disabled: Boolean(disabled),
  };
  const text = value == null ? '' : String(value);

  switch (field.type) {
    case 'toggle':
      return (
        <Toggle
          id={id}
          labelText={labelText}
          disabled={Boolean(disabled)}
          labelA={t('admin.no')}
          labelB={t('admin.yes')}
          toggled={Boolean(value)}
          onToggle={onChange}
        />
      );
    case 'textarea':
      return <TextArea {...common} value={text} onChange={(e) => onChange(e.target.value || null)} />;
    case 'json': {
      const invalidJson = !isJsonObject(text) && text !== '';
      return (
        <TextArea
          {...common}
          className="pos-admin-form__code"
          invalid={Boolean(invalidText) || invalidJson}
          invalidText={invalidText ?? t('admin.jsonInvalid')}
          value={text}
          rows={3}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    case 'number':
    case 'decimal':
      return (
        <TextInput
          {...common}
          type="number"
          step={field.type === 'decimal' ? 'any' : 1}
          value={text}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case 'date':
      return (
        <DateField
          id={id}
          labelText={labelText}
          invalid={Boolean(invalidText)}
          invalidText={invalidText}
          value={text.slice(0, 10)}
          onChange={(v) => onChange(v || null)}
        />
      );
    case 'time':
      return (
        <TextInput
          {...common}
          type="time"
          value={text.slice(0, 5)}
          onChange={(e) => onChange(e.target.value ? `${e.target.value}:00` : null)}
        />
      );
    case 'datetime':
      return (
        <TextInput
          {...common}
          type="datetime-local"
          value={text ? toLocalInput(text) : ''}
          onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
        />
      );
    case 'select':
      return (
        <Select {...common} value={text} onChange={(e) => onChange(parseOption(field, e.target.value))}>
          <SelectItem value="" text={field.required ? t('admin.choose') : t('admin.none')} />
          {field.options?.map((option) => (
            <SelectItem key={String(option.value)} value={String(option.value)} text={t(option.label)} />
          ))}
        </Select>
      );
    case 'reference':
      return (
        <ReferenceInput
          field={field}
          value={value}
          invalidText={invalidText}
          marking={marking}
          disabled={disabled}
          values={values}
          onChange={onChange}
        />
      );
    case 'localized':
      return (
        <LocalizedInput
          id={id}
          labelText={labelText}
          invalid={Boolean(invalidText)}
          value={value}
          onChange={onChange}
        />
      );
    case 'weekdays': {
      const mask = typeof value === 'number' ? value : 127;
      return (
        <FormGroup legendText={labelText} invalid={Boolean(invalidText)}>
          <div className="pos-admin-form__weekdays">
            {WEEKDAYS.map((day, index) => (
              <Checkbox
                key={day}
                id={`${id}-${day}`}
                labelText={t(`admin.weekdays.${day}`)}
                checked={(mask & (1 << index)) !== 0}
                onChange={(_e, { checked }) => onChange(checked ? mask | (1 << index) : mask & ~(1 << index))}
              />
            ))}
          </div>
        </FormGroup>
      );
    }
    default:
      return <TextInput {...common} value={text} onChange={(e) => onChange(e.target.value)} />;
  }
}

function ReferenceInput({ field, value, invalidText, marking, disabled, values, onChange }: FieldInputProps) {
  const { t } = useTranslation();
  const marked = useMarkedLabel();
  const limit = field.limitTo;
  const allowed = useAllowedValues(limit, values);
  // Limited by another field: nothing to offer until it has a value, then only what it allows.
  const waiting = Boolean(limit && !values?.[limit.field]);
  // A value the other field no longer allows (another item was chosen, or its unit was switched off) is cleared.
  const stale = Boolean(limit && allowed.data && value != null && value !== '' && !allowed.data.has(String(value)));
  useEffect(() => {
    if (stale) onChange(null);
  }, [stale, onChange]);
  return (
    <ReferenceComboBox
      id={`field-${field.key}`}
      titleText={marked(t(field.label), field.required, marking)}
      helperText={field.helper ? t(field.helper) : undefined}
      disabled={Boolean(disabled) || waiting}
      placeholder={
        waiting && limit
          ? t('admin.chooseFirst', { field: t(limit.fieldLabel) })
          : limit && allowed.isLoading
            ? t('common.loading')
            : undefined
      }
      resourceKey={field.ref}
      value={value == null ? null : String(value)}
      allowed={limit ? (allowed.data ?? new Set<string>()) : undefined}
      invalid={Boolean(invalidText)}
      invalidText={invalidText}
      onChange={onChange}
    />
  );
}

function parseOption(field: FieldDef, raw: string) {
  if (raw === '') return null;
  const option = field.options?.find((o) => String(o.value) === raw);
  return option ? option.value : raw;
}

function isJsonObject(text: string) {
  try {
    const parsed: unknown = JSON.parse(text);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
  } catch {
    return false;
  }
}

function toLocalInput(iso: string) {
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** One text box per language the admin app edits texts in (the staff languages unless it says otherwise). */
function LocalizedInput({
  id,
  labelText,
  invalid,
  value,
  onChange,
}: {
  id: string;
  labelText: string;
  invalid: boolean;
  value: unknown;
  onChange(value: unknown): void;
}) {
  const languages = useContext(AdminContext)?.textLanguages ?? LANGUAGES.map((l) => l.code);
  const texts = (value ?? {}) as Record<string, string>;
  return (
    <FormGroup legendText={labelText} invalid={invalid}>
      {languages.map((code) => (
        <TextInput
          key={code}
          id={`${id}-${code}`}
          labelText={languageName(code)}
          value={texts[code] ?? ''}
          onChange={(e) => onChange({ ...texts, [code]: e.target.value })}
        />
      ))}
    </FormGroup>
  );
}
